import { useAppReady } from "@components/utils/AppLoadingSkeleton";
import { getAwsRum } from "@configs/aws-rum/loader";
import { capturePageCrashError } from "@configs/sentry/utils";
import { STORAGE_KEY } from "@constants";
import useCrashSessionCleanup from "@hooks/useCrashSessionCleanup";
import { safeSessionSetItem } from "@utils/storage";
import { useEffect, useLayoutEffect, useState } from "react";
import { ErrorFallbackProps } from "./types";
import { getCrashFlowState } from "./utils";

const ERROR_FALLBACK_CLEANUP_KEYS = [STORAGE_KEY.PAGE_CRASH_AUTO_RELOAD_COUNT] as const;

const ErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
  useAppReady();
  const [eventId, setEventId] = useState<string | undefined>();
  const [shouldAutoReload, setShouldAutoReload] = useState(false);

  useLayoutEffect(() => {
    const { canAutoReload, reloadCount } = getCrashFlowState(error);
    setShouldAutoReload(canAutoReload);

    if (!canAutoReload) return;

    safeSessionSetItem(STORAGE_KEY.PAGE_CRASH_AUTO_RELOAD_COUNT, String(reloadCount + 1));
    window.location.reload();
  }, [error]);
  useEffect(() => {
    if (!error) return;

    const { canAutoReload, isAutoReloadCandidate, reloadCount } = getCrashFlowState(error);
    if (canAutoReload) return;

    let isCancelled = false;

    const reportCrash = async () => {
      const id = await capturePageCrashError(error, {
        auto_reload_candidate: isAutoReloadCandidate,
        auto_reload_count: reloadCount,
      });

      if (isCancelled) return;

      if (id) {
        safeSessionSetItem(STORAGE_KEY.PAGE_CRASH_SENTRY_EVENT_ID, id);
      }
      setEventId(id);

      const rum = await getAwsRum();
      if (!isCancelled) {
        rum?.recordError?.(error);
      }
    };

    void reportCrash();

    return () => {
      isCancelled = true;
    };
  }, [error]);

  useCrashSessionCleanup({
    enabled: !shouldAutoReload,
    keys: ERROR_FALLBACK_CLEANUP_KEYS,
  });

  const handleRetry = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  if (shouldAutoReload) {
    return null;
  }

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center px-2 py-8 sm:px-6">
      <div className="flex w-full max-w-xs flex-col items-center">
        {/* Error Title */}
        <h1 className="mb-3 text-center text-xl font-bold text-content-base">Something went wrong</h1>

        {/* Error Message */}
        <p className="mb-2 text-center text-sm text-content-soft">
          We&apos;re sorry, but something unexpected happened. Please try again.
        </p>
        {eventId ? (
          <p className="mb-6 text-center text-xs text-content-soft">Trace ID: {eventId}</p>
        ) : (
          <div className="mb-6" />
        )}

        {/* Action Buttons */}
        <div className="flex w-full flex-col gap-3">
          <button
            onClick={handleRetry}
            data-element-id="button-try-again"
            className="flex h-[46px] w-full items-center justify-center rounded-lg bg-grdPrimary text-sm font-semibold leading-[19px] transition-all duration-200 will-change-auto active:bg-grdPrimaryAlt disabled:pointer-events-none disabled:bg-grdPrimaryAlt disabled:opacity-40"
          >
            Try Again
          </button>

          <button
            onClick={handleGoHome}
            data-element-id="button-go-home"
            className="w-full rounded-lg border border-stroke-base bg-transparent px-4 py-3 text-sm font-semibold text-content-base transition-colors hover:bg-stroke-soft active:bg-stroke-base"
          >
            Go to Home
          </button>
        </div>

        {/* Error Details (Development Only) */}
        {Boolean(error) && process.env.NODE_ENV === "development" && (
          <details className="mt-6 w-full max-w-xs">
            <summary className="cursor-pointer text-xs text-content-soft">Error Details</summary>
            <pre className="mt-2 whitespace-pre-wrap rounded bg-stroke-soft p-3 text-xs text-content-base">
              {error instanceof Error ? error.message : String(error)}
              {error instanceof Error && error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default ErrorFallback;
